import { FeedbackAlert as Alert } from "src/shared/ui/src/feedback/FeedbackAlert";
import {
  Box,
  Button,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronLeft as PreviousFrameIcon,
  ChevronRight as NextFrameIcon,
  Close as HideIcon,
  OpenInFull as ShowIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  SettingsRemote as ControlRoomIcon,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getWebSocketUrl } from "src/config";
import ColorPicker from "src/shared/ui/src/components/ColorPicker";
import { ControlRoomCardUI } from "src/shared/ui/src/control-room/ControlRoomCard";
import {
  CompiledLayoutRenderer,
  extractControlRoomCapabilities,
  LayoutDataProvider,
  RuntimeLayoutFullscreenShell,
  type GetLayoutFontFaces,
} from "src/shared/ui/src/display-editor";
import { keyboardEventToCombo } from "src/shared/util/src/blueprint/shortcut-keys";
import {
  buildSnapshotCards,
  type ControlRoomCard,
} from "src/shared/util/src/control-room/protocol";
import {
  createLayoutCompetitionPreviewFrames,
  type CompetitionPreviewAgeCategoryCode,
  type CompetitionPreviewSexProfile,
  type LayoutDocument,
  validateLayout,
} from "src/shared/util/src/layout";
import type { RuntimeControlRoomDispatch } from "src/shared/util/src/layout-compiler/types";
import dico from "src/shared/util/src/language/dico";
import type { ThemeColors } from "src/shared/util/src/types";
import { useUserStore } from "src/stores/userStore";
import { useUserLayoutStore } from "src/stores/userLayoutStore";

const runtimeGlobalStyles = (
  <GlobalStyles
    styles={{
      ":root": {
        backgroundColor: "rgba(255, 255, 255, 0)",
      },
      "*": {
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
      },
      "html, body": {
        margin: 0,
        padding: 0,
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "transparent",
      },
      "#root": {
        height: "100vh",
        overflow: "hidden",
        margin: 0,
        padding: 0,
      },
    }}
  />
);

const SEX_PROFILES: Array<{
  value: CompetitionPreviewSexProfile;
  labelKey: string;
  fallback: string;
}> = [
  { value: "mixed", labelKey: "sexProfileMixed", fallback: "2 groupes mixtes" },
  { value: "men", labelKey: "sexProfileMen", fallback: "2 groupes hommes" },
  {
    value: "women",
    labelKey: "sexProfileWomen",
    fallback: "2 groupes femmes",
  },
  {
    value: "menWomen",
    labelKey: "sexProfileMenWomen",
    fallback: "Groupe hommes + groupe femmes",
  },
  {
    value: "mixedMen",
    labelKey: "sexProfileMixedMen",
    fallback: "Groupe mixte + groupe hommes",
  },
  {
    value: "mixedWomen",
    labelKey: "sexProfileMixedWomen",
    fallback: "Groupe mixte + groupe femmes",
  },
];

/** Default theme injected by the competition preview simulator. */
const DEFAULT_PREVIEW_THEME_COLORS: ThemeColors = {
  primary: "#0056B3",
  primaryText: "#ffffff",
  background: "#ffffff",
  backgroundText: "#000000",
  rankingText: "#000000",
  ranking: "#ffffff",
};

const THEME_COLOR_FIELDS: Array<{
  key: keyof ThemeColors;
  labelKey:
    | "primaryColor"
    | "primaryTextColor"
    | "backgroundColor"
    | "backgroundTextColor"
    | "rankingColor"
    | "rankingTextColor";
}> = [
  { key: "primary", labelKey: "primaryColor" },
  { key: "primaryText", labelKey: "primaryTextColor" },
  { key: "background", labelKey: "backgroundColor" },
  { key: "backgroundText", labelKey: "backgroundTextColor" },
  { key: "ranking", labelKey: "rankingColor" },
  { key: "rankingText", labelKey: "rankingTextColor" },
];

const AGE_CATEGORIES: Array<{
  value: CompetitionPreviewAgeCategoryCode;
  label: string;
}> = [
  { value: "SJR", label: "Sub-Junior" },
  { value: "JR", label: "Junior" },
  { value: "SNR", label: "Senior" },
  { value: "M1", label: "Master 1" },
  { value: "M2", label: "Master 2" },
  { value: "M3", label: "Master 3" },
  { value: "M4", label: "Master 4" },
];

export default function LayoutDisplayPreviewPage() {
  const { layoutId } = useParams<{ layoutId: string }>();
  const token = useUserStore((state) => state.token);
  const getLayout = useUserLayoutStore((state) => state.getLayout);
  const listLayoutFonts = useUserLayoutStore((state) => state.listLayoutFonts);
  const previewDico =
    (
      dico().licensedPortal.layoutEditor as {
        runtimePreview?: Record<string, string>;
      }
    ).runtimePreview ?? {};
  const [layout, setLayout] = useState<LayoutDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frameIndex, setFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [secondsPerFrame, setSecondsPerFrame] = useState(5);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [controlRoomVisible, setControlRoomVisible] = useState(true);
  const controlRoomDispatchIdRef = useRef(0);
  const [controlRoomDispatches, setControlRoomDispatches] = useState<
    RuntimeControlRoomDispatch[]
  >([]);
  const [sexProfile, setSexProfile] =
    useState<CompetitionPreviewSexProfile>("mixed");
  const [group1Age, setGroup1Age] =
    useState<CompetitionPreviewAgeCategoryCode>("SNR");
  const [group2Age, setGroup2Age] =
    useState<CompetitionPreviewAgeCategoryCode>("SNR");
  const [themeColors, setThemeColors] = useState<ThemeColors>(
    DEFAULT_PREVIEW_THEME_COLORS,
  );

  useEffect(() => {
    let cancelled = false;
    if (!layoutId || !token) {
      setError(previewDico.loadError ?? "Unable to load preview.");
      return;
    }

    void (async () => {
      const nextLayout = await getLayout(token, layoutId);
      if (cancelled) return;
      if (!nextLayout) {
        setError(previewDico.loadError ?? "Unable to load preview.");
        return;
      }
      const validation = validateLayout(nextLayout);
      if (!validation.valid) {
        setError(validation.errors[0] ?? "Invalid layout.");
        return;
      }
      setLayout(nextLayout);
      setError(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [getLayout, layoutId, previewDico.loadError, token]);

  const preview = useMemo(
    () =>
      createLayoutCompetitionPreviewFrames({
        sexProfile,
        groupAgeCategoryCodes: [group1Age, group2Age],
      }),
    [group1Age, group2Age, sexProfile],
  );
  const frames = preview.frames;
  const currentFrame = frames[Math.min(frameIndex, frames.length - 1)];
  const displayData = useMemo(() => {
    if (!currentFrame) return null;
    return {
      ...currentFrame.data,
      themeColors,
    };
  }, [currentFrame, themeColors]);
  const controlRoomDico = (dico() as Record<string, unknown>).controlRoom as
    | Record<string, string>
    | undefined;
  const controlRoomCards = useMemo<ControlRoomCard[]>(() => {
    if (!layout) return [];
    const capabilities = extractControlRoomCapabilities(layout);
    return buildSnapshotCards(
      new Map([
        [
          "layout-preview",
          {
            meetId: "layout-preview",
            targetId: "layout-preview",
            targetLabel:
              previewDico.controlRoomPreviewTarget ?? "Preview compétition",
            capabilities,
          },
        ],
      ]),
    );
  }, [layout, previewDico.controlRoomPreviewTarget]);

  useEffect(() => {
    setFrameIndex((current) =>
      Math.min(current, Math.max(0, frames.length - 1)),
    );
  }, [frames.length]);

  useEffect(() => {
    if (!isPlaying || frames.length <= 1) return;
    const interval = window.setInterval(
      () => {
        setFrameIndex((current) => {
          if (current >= frames.length - 1) {
            setIsPlaying(false);
            return current;
          }
          return current + 1;
        });
      },
      Math.max(0.25, secondsPerFrame) * 1000,
    );
    return () => window.clearInterval(interval);
  }, [frames.length, isPlaying, secondsPerFrame]);

  const loadLayoutFontFaces = useCallback<GetLayoutFontFaces>(async () => {
    if (!token) return null;
    const fonts = await listLayoutFonts(token);
    if (fonts.length === 0) return null;
    return {
      apiUrl: getWebSocketUrl(),
      fonts,
      token,
    };
  }, [listLayoutFonts, token]);

  const handleThemeColorChange = useCallback(
    (key: keyof ThemeColors, value: string) => {
      setThemeColors((current) => ({ ...current, [key]: value }));
    },
    [],
  );

  const resetThemeColors = useCallback(() => {
    setThemeColors(DEFAULT_PREVIEW_THEME_COLORS);
  }, []);

  const goToFrame = useCallback(
    (nextFrameIndex: number) => {
      setFrameIndex(Math.max(0, Math.min(frames.length - 1, nextFrameIndex)));
    },
    [frames.length],
  );

  const handleControlRoomTrigger = useCallback((combo: string) => {
    setControlRoomDispatches([
      { id: ++controlRoomDispatchIdRef.current, combo },
    ]);
  }, []);

  useEffect(() => {
    if (controlRoomCards.length === 0) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const combo = keyboardEventToCombo(event);
      if (!combo || !controlRoomCards.some((card) => card.combo === combo)) {
        return;
      }

      event.preventDefault();
      handleControlRoomTrigger(combo);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [controlRoomCards, handleControlRoomTrigger]);

  return (
    <>
      {runtimeGlobalStyles}
      <RuntimeLayoutFullscreenShell>
        {layout && displayData ? (
          <LayoutDataProvider
            data={displayData}
            getLayoutFontFaces={loadLayoutFontFaces}
          >
            <CompiledLayoutRenderer
              document={layout}
              data={displayData}
              scale={1}
              controlRoomDispatches={controlRoomDispatches}
            />
          </LayoutDataProvider>
        ) : (
          <Box
            sx={{
              width: "100vw",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#111827",
              color: "#fff",
            }}
          >
            <Typography variant="h5">
              {error ?? previewDico.loading ?? "Loading preview..."}
            </Typography>
          </Box>
        )}
        {overlayVisible ? (
          <PreviewOverlay
            ageDico={previewDico}
            currentFrameLabel={currentFrame?.label ?? ""}
            frameIndex={frameIndex}
            frameCount={frames.length}
            group1Age={group1Age}
            group2Age={group2Age}
            isPlaying={isPlaying}
            secondsPerFrame={secondsPerFrame}
            sexProfile={sexProfile}
            onFrameChange={goToFrame}
            onGroup1AgeChange={setGroup1Age}
            onGroup2AgeChange={setGroup2Age}
            onHide={() => setOverlayVisible(false)}
            onPlayToggle={() => setIsPlaying((current) => !current)}
            onResetThemeColors={resetThemeColors}
            onSecondsPerFrameChange={setSecondsPerFrame}
            onSexProfileChange={setSexProfile}
            onThemeColorChange={handleThemeColorChange}
            themeColors={themeColors}
          />
        ) : (
          <Tooltip title={previewDico.showControls ?? "Show controls"}>
            <IconButton
              size="small"
              onClick={() => setOverlayVisible(true)}
              sx={{
                position: "fixed",
                right: 16,
                bottom: 16,
                zIndex: 100000,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.14)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.16)",
                color: "#0f172a",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              <ShowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {controlRoomVisible ? (
          <PreviewControlRoomOverlay
            cards={controlRoomCards}
            controlRoomDico={controlRoomDico ?? {}}
            previewDico={previewDico}
            onHide={() => setControlRoomVisible(false)}
            onTrigger={handleControlRoomTrigger}
          />
        ) : (
          <Tooltip
            title={previewDico.showControlRoom ?? controlRoomDico?.openButton}
          >
            <IconButton
              size="small"
              onClick={() => setControlRoomVisible(true)}
              sx={{
                position: "fixed",
                top: 16,
                right: 16,
                zIndex: 100000,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.14)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.16)",
                color: "#0f172a",
                "&:hover": {
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              <ControlRoomIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </RuntimeLayoutFullscreenShell>
    </>
  );
}

function PreviewControlRoomOverlay({
  cards,
  controlRoomDico,
  previewDico,
  onHide,
  onTrigger,
}: {
  cards: ControlRoomCard[];
  controlRoomDico: Record<string, string>;
  previewDico: Record<string, string>;
  onHide: () => void;
  onTrigger: (combo: string) => void;
}) {
  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 100000,
        width: "min(420px, calc(100vw - 32px))",
        maxHeight: "calc(100vh - 160px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        p: 2,
        borderRadius: 1,
        backgroundColor: "rgba(248, 250, 252, 0.97)",
        border: "1px solid rgba(15, 23, 42, 0.12)",
        color: "#0f172a",
        boxShadow: "0 14px 36px rgba(15, 23, 42, 0.2)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            color: "#1976d2",
            backgroundColor: "rgba(25, 118, 210, 0.1)",
            border: "1px solid rgba(25, 118, 210, 0.16)",
          }}
        >
          <ControlRoomIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            {previewDico.controlRoomTitle ?? controlRoomDico.title}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }} noWrap>
            {previewDico.controlRoomSubtitle ?? "Actions locales de la preview"}
          </Typography>
        </Box>
        <Tooltip
          title={previewDico.hideControlRoom ?? controlRoomDico.openButton}
        >
          <IconButton size="small" onClick={onHide} sx={previewIconButtonSx}>
            <HideIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ overflowY: "auto", pr: 0.5 }}>
        {cards.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 1 }}>
            {previewDico.controlRoomNoCards ?? controlRoomDico.noCards}
          </Alert>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1.5,
            }}
          >
            {cards.map((card) => (
              <ControlRoomCardUI
                key={`${card.combo}\0${card.name}\0${card.description}`}
                card={card}
                onTrigger={onTrigger}
                disabled={false}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function PreviewOverlay({
  ageDico,
  currentFrameLabel,
  frameIndex,
  frameCount,
  group1Age,
  group2Age,
  isPlaying,
  secondsPerFrame,
  sexProfile,
  themeColors,
  onFrameChange,
  onGroup1AgeChange,
  onGroup2AgeChange,
  onHide,
  onPlayToggle,
  onResetThemeColors,
  onSecondsPerFrameChange,
  onSexProfileChange,
  onThemeColorChange,
}: {
  ageDico: Record<string, string>;
  currentFrameLabel: string;
  frameIndex: number;
  frameCount: number;
  group1Age: CompetitionPreviewAgeCategoryCode;
  group2Age: CompetitionPreviewAgeCategoryCode;
  isPlaying: boolean;
  secondsPerFrame: number;
  sexProfile: CompetitionPreviewSexProfile;
  themeColors: ThemeColors;
  onFrameChange: (nextFrameIndex: number) => void;
  onGroup1AgeChange: (value: CompetitionPreviewAgeCategoryCode) => void;
  onGroup2AgeChange: (value: CompetitionPreviewAgeCategoryCode) => void;
  onHide: () => void;
  onPlayToggle: () => void;
  onResetThemeColors: () => void;
  onSecondsPerFrameChange: (value: number) => void;
  onSexProfileChange: (value: CompetitionPreviewSexProfile) => void;
  onThemeColorChange: (key: keyof ThemeColors, value: string) => void;
}) {
  const themeLabels = dico().theme;
  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 100000,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid rgba(15, 23, 42, 0.12)",
        color: "#0f172a",
        boxShadow: "0 12px 34px rgba(15, 23, 42, 0.18)",
        backdropFilter: "blur(8px)",
        maxHeight: "calc(100vh - 32px)",
        overflowY: "auto",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={ageDico.previousFrame ?? "Previous frame"}>
            <span>
              <IconButton
                size="small"
                onClick={() => onFrameChange(frameIndex - 1)}
                disabled={frameIndex <= 0}
                sx={previewIconButtonSx}
              >
                <PreviousFrameIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              isPlaying ? (ageDico.pause ?? "Pause") : (ageDico.play ?? "Play")
            }
          >
            <IconButton
              size="small"
              onClick={onPlayToggle}
              sx={previewPrimaryIconButtonSx}
            >
              {isPlaying ? (
                <PauseIcon fontSize="small" />
              ) : (
                <PlayIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={ageDico.nextFrame ?? "Next frame"}>
            <span>
              <IconButton
                size="small"
                onClick={() => onFrameChange(frameIndex + 1)}
                disabled={frameIndex >= frameCount - 1}
                sx={previewIconButtonSx}
              >
                <NextFrameIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{ minWidth: 96, fontVariantNumeric: "tabular-nums" }}
          >
            {frameIndex + 1} / {frameCount}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              color: "#475569",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentFrameLabel}
          </Typography>
          <Tooltip title={ageDico.hideControls ?? "Hide controls"}>
            <IconButton size="small" onClick={onHide} sx={previewIconButtonSx}>
              <HideIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Slider
            min={0}
            max={Math.max(0, frameCount - 1)}
            value={frameIndex}
            onChange={(_, value) =>
              onFrameChange(Array.isArray(value) ? value[0] : value)
            }
            sx={{
              flex: 1,
              color: "#1976d2",
              minWidth: { md: 240 },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: "#64748b" }}>
              {ageDico.sexProfile ?? "Profil"}
            </InputLabel>
            <Select
              value={sexProfile}
              label={ageDico.sexProfile ?? "Profil"}
              onChange={(event) =>
                onSexProfileChange(
                  event.target.value as CompetitionPreviewSexProfile,
                )
              }
              sx={selectSx}
            >
              {SEX_PROFILES.map((profile) => (
                <MenuItem key={profile.value} value={profile.value}>
                  {ageDico[profile.labelKey] ?? profile.fallback}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <AgeSelect
            label={ageDico.group1Age ?? "Age groupe 1"}
            value={group1Age}
            onChange={onGroup1AgeChange}
          />
          <AgeSelect
            label={ageDico.group2Age ?? "Age groupe 2"}
            value={group2Age}
            onChange={onGroup2AgeChange}
          />
          <Box sx={{ width: { xs: "100%", md: 160 } }}>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {ageDico.secondsPerFrame ?? "Secondes / frame"}: {secondsPerFrame}
              s
            </Typography>
            <Slider
              min={0.5}
              max={15}
              step={0.5}
              value={secondsPerFrame}
              onChange={(_, value) =>
                onSecondsPerFrameChange(Array.isArray(value) ? value[0] : value)
              }
              sx={{ color: "#2e7d32" }}
            />
          </Box>
        </Stack>

        <Stack spacing={0.75}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {ageDico.themeColors ?? themeLabels.colors}
            </Typography>
            <Button
              size="small"
              onClick={onResetThemeColors}
              sx={{ textTransform: "none", color: "#475569", minWidth: 0 }}
            >
              {ageDico.resetThemeColors ?? themeLabels.reset}
            </Button>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 1,
            }}
          >
            {THEME_COLOR_FIELDS.map((field) => (
              <Box key={field.key}>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", display: "block", mb: 0.5 }}
                >
                  {themeLabels[field.labelKey]}
                </Typography>
                <ColorPicker
                  value={themeColors[field.key]}
                  onChange={(nextColor) =>
                    onThemeColorChange(field.key, nextColor)
                  }
                />
              </Box>
            ))}
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}

function AgeSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: CompetitionPreviewAgeCategoryCode;
  onChange: (value: CompetitionPreviewAgeCategoryCode) => void;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 132 }}>
      <InputLabel sx={{ color: "#64748b" }}>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(event) =>
          onChange(event.target.value as CompetitionPreviewAgeCategoryCode)
        }
        sx={selectSx}
      >
        {AGE_CATEGORIES.map((category) => (
          <MenuItem key={category.value} value={category.value}>
            {category.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const selectSx = {
  color: "#0f172a",
  backgroundColor: "#ffffff",
  ".MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(15, 23, 42, 0.18)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(25, 118, 210, 0.62)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1976d2",
  },
  ".MuiSvgIcon-root": {
    color: "#475569",
  },
};

const previewIconButtonSx = {
  width: 32,
  height: 32,
  border: "1px solid rgba(15, 23, 42, 0.14)",
  color: "#334155",
  backgroundColor: "#ffffff",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "rgba(25, 118, 210, 0.4)",
  },
  "&.Mui-disabled": {
    color: "#cbd5e1",
    borderColor: "rgba(15, 23, 42, 0.08)",
  },
};

const previewPrimaryIconButtonSx = {
  ...previewIconButtonSx,
  color: "#ffffff",
  backgroundColor: "#1976d2",
  borderColor: "#1976d2",
  "&:hover": {
    backgroundColor: "#1565c0",
    borderColor: "#1565c0",
  },
};
